/**
 * 좌석 배치 알고리즘 서비스
 */
import { Seat, SeatModel, LayoutType, Position } from '../models/Seat.js';

/**
 * 배치 결과
 */
export interface LayoutResult {
    /** 배치된 좌석 배열 */
    seats: Seat[];
    /** 성공 여부 */
    success: boolean;
    /** 에러 메시지 (실패시) */
    errorMessage?: string;
}

/**
 * 배치 알고리즘을 담당하는 서비스
 */
export class LayoutService {
    /**
     * 1열 균등 배치를 생성합니다.
     * 학생 총 인원수를 분단 수로 나눈 숫자만큼의 좌석을 일렬로 균등하게 배치
     * @param totalSeats 전체 좌석 수
     * @param partitionCount 분단 수
     * @param canvasWidth 캔버스 너비
     * @param canvasHeight 캔버스 높이
     * @returns 배치된 좌석 배열
     */
    public static createSingleUniformLayout(
        totalSeats: number, 
        canvasWidth: number = 800, 
        canvasHeight: number = 600,
        partitionCount: number = 1
    ): Seat[] {
        const seats: Seat[] = [];
        
        // 분단별 좌석 수 계산
        const seatsPerPartition = Math.ceil(totalSeats / partitionCount);
        
        // 좌석 간격 설정
        const seatWidth = 60;
        const seatHeight = 60;
        const horizontalSpacing = 80;
        const verticalSpacing = 100;
        
        // 좌석 생성
        let seatIndex = 0;
        for (let partition = 0; partition < partitionCount && seatIndex < totalSeats; partition++) {
            // 분단별 Y 위치 계산
            const startY = 150 + partition * (verticalSpacing * (Math.ceil(seatsPerPartition / 4) - 1) + 100);
            
            // 분단 내 좌석을 4열로 배치
            for (let i = 0; i < seatsPerPartition && seatIndex < totalSeats; i++) {
                const row = Math.floor(i / 4);
                const col = i % 4;
                
                const position: Position = {
                    x: (canvasWidth / 2) - (1.5 * horizontalSpacing) + (col * horizontalSpacing),
                    y: startY + row * verticalSpacing
                };
                
                const seat = SeatModel.create(position);
                seats.push(seat);
                seatIndex++;
            }
        }
        
        return seats;
    }

    /**
     * 2열 균등 배치(짝꿍 책상)를 생성합니다.
     * 두 명의 좌석을 나란히 붙여서 배치하고, 분단 수만큼 나누어서 배치
     * @param totalSeats 전체 좌석 수
     * @param canvasWidth 캔버스 너비
     * @param canvasHeight 캔버스 높이
     * @param partitionCount 분단 수
     * @returns 배치된 좌석 배열
     */
    public static createPairUniformLayout(
        totalSeats: number,
        canvasWidth: number = 800,
        canvasHeight: number = 600,
        partitionCount: number = 1
    ): Seat[] {
        const seats: Seat[] = [];
        
        // 짝꿍 책상 쌍의 수
        const pairCount = Math.ceil(totalSeats / 2);
        const pairsPerPartition = Math.ceil(pairCount / partitionCount);
        
        const seatWidth = 60;
        const seatHeight = 60;
        const pairSpacing = 20; // 짝꿍 사이 간격
        const horizontalSpacing = 140; // 다음 쌍까지의 가로 간격
        const verticalSpacing = 80;
        const partitionGap = 150; // 분단 사이 간격
        
        // 좌석 생성
        let pairIndex = 0;
        for (let partition = 0; partition < partitionCount && pairIndex < pairCount; partition++) {
            // 분단별 Y 위치 계산
            const startY = 150 + partition * (verticalSpacing * (Math.ceil(pairsPerPartition / 4) - 1) + partitionGap);
            
            // 분단 내 짝꿍 배치 (4열로)
            for (let i = 0; i < pairsPerPartition && pairIndex < pairCount; i++) {
                const row = Math.floor(i / 4);
                const col = i % 4;
                
                // 짝꿍 쌍의 시작 X 위치
                const baseX = (canvasWidth / 2) - (1.5 * horizontalSpacing) + (col * horizontalSpacing);
                const y = startY + row * verticalSpacing;
                
                // 왼쪽 좌석
                const seat1 = SeatModel.create({ x: baseX, y });
                
                // 오른쪽 좌석
                const seat2 = SeatModel.create({ 
                    x: baseX + seatWidth + pairSpacing, 
                    y 
                });
                
                // 짝꿍 연결
                seat1.pairSeatId = seat2.id;
                seat2.pairSeatId = seat1.id;
                
                seats.push(seat1, seat2);
                pairIndex++;
            }
        }
        
        // 실제 좌석 수만큼만 반환
        return seats.slice(0, totalSeats);
    }

    /**
     * 모둠 배치를 생성합니다.
     * @param totalSeats 전체 좌석 수
     * @param groupSize 모둠 크기 (기본값: 4)
     * @param canvasWidth 캔버스 너비
     * @param canvasHeight 캔버스 높이
     * @returns 배치된 좌석 배열
     */
    public static createGroupLayout(
        totalSeats: number,
        groupSize: number = 4,
        canvasWidth: number = 800,
        canvasHeight: number = 600
    ): Seat[] {
        const seats: Seat[] = [];
        const groupCount = Math.ceil(totalSeats / groupSize);
        
        // 모둠을 2열로 배치
        const cols = Math.ceil(Math.sqrt(groupCount));
        const rows = Math.ceil(groupCount / cols);
        
        const seatWidth = 60;
        const seatHeight = 60;
        const withinGroupSpacing = 20;
        /** 모둠과 모둠 사이의 여백 */
        const groupGap = 100;

        // 모둠 하나가 차지하는 실제 크기에서 모둠 간격을 계산한다.
        // 간격을 고정값(180)으로 두면 모둠이 그보다 커지는 5명/6명 배치에서
        // 옆 모둠의 좌석과 물리적으로 겹친다.
        const seatsPerRow = Math.ceil(Math.sqrt(groupSize));
        const seatsPerCol = Math.ceil(groupSize / seatsPerRow);
        const groupWidth = (seatsPerRow - 1) * (seatWidth + withinGroupSpacing) + seatWidth;
        const groupHeight = (seatsPerCol - 1) * (seatHeight + withinGroupSpacing) + seatHeight;
        const groupSpacingX = groupWidth + groupGap;
        const groupSpacingY = groupHeight + groupGap;

        const totalWidth = (cols - 1) * groupSpacingX + groupWidth;
        const totalHeight = (rows - 1) * groupSpacingY + groupHeight;
        const startX = (canvasWidth - totalWidth) / 2;
        const startY = (canvasHeight - totalHeight) / 2;
        
        let seatIndex = 0;
        for (let groupRow = 0; groupRow < rows && seatIndex < totalSeats; groupRow++) {
            for (let groupCol = 0; groupCol < cols && seatIndex < totalSeats; groupCol++) {
                const groupStartX = startX + groupCol * groupSpacingX;
                const groupStartY = startY + groupRow * groupSpacingY;
                
                // 모둠 내 좌석 배치 (2x2 또는 적절한 형태)
                for (let i = 0; i < groupSize && seatIndex < totalSeats; i++) {
                    const row = Math.floor(i / seatsPerRow);
                    const col = i % seatsPerRow;
                    
                    const position: Position = {
                        x: groupStartX + col * (seatWidth + withinGroupSpacing),
                        y: groupStartY + row * (seatHeight + withinGroupSpacing)
                    };
                    
                    const seat = SeatModel.create(position);
                    seats.push(seat);
                    seatIndex++;
                }
            }
        }
        
        return seats;
    }

    /**
     * ㄷ자 배치를 생성합니다.
     * @param totalSeats 전체 좌석 수
     * @param canvasWidth 캔버스 너비
     * @param canvasHeight 캔버스 높이
     * @returns 배치된 좌석 배열
     */
    public static createUShapeLayout(
        totalSeats: number,
        canvasWidth: number = 800
    ): Seat[] {
        const seats: Seat[] = [];

        if (totalSeats === 0) return seats;

        // ㄷ자 모양: 왼쪽 측면 + 아래쪽(앞) + 오른쪽 측면
        // 좌석을 3면에 균등 분배
        const bottomLength = Math.ceil(totalSeats / 3);
        const remaining = totalSeats - bottomLength;
        const leftLength = Math.ceil(remaining / 2);
        const rightLength = remaining - leftLength;

        const spacing = 80;
        const seatWidth = 60;

        // ㄷ자 전체 크기 계산
        const uWidth = (bottomLength - 1) * spacing + seatWidth;
        const uHeight = (Math.max(leftLength, rightLength) - 1) * spacing + seatWidth;

        // 캔버스 중앙 정렬
        const startX = (canvasWidth - uWidth) / 2;
        const startY = 100;

        let seatIndex = 0;

        // 왼쪽 측면 (위에서 아래로)
        for (let i = 0; i < leftLength && seatIndex < totalSeats; i++) {
            seats.push(SeatModel.create({
                x: startX,
                y: startY + i * spacing
            }));
            seatIndex++;
        }

        // 아래쪽 (왼쪽에서 오른쪽으로, 교탁 반대편)
        for (let i = 0; i < bottomLength && seatIndex < totalSeats; i++) {
            seats.push(SeatModel.create({
                x: startX + i * spacing,
                y: startY + uHeight
            }));
            seatIndex++;
        }

        // 오른쪽 측면 (아래에서 위로, 아래쪽 좌석과 겹치지 않도록 한 칸 위부터)
        for (let i = 0; i < rightLength && seatIndex < totalSeats; i++) {
            seats.push(SeatModel.create({
                x: startX + uWidth - seatWidth,
                y: startY + uHeight - (i + 1) * spacing
            }));
            seatIndex++;
        }

        return seats;
    }

    /**
     * 특정 배치 유형에 맞는 좌석 배열을 생성합니다.
     * @param layoutType 배치 유형
     * @param totalSeats 전체 좌석 수
     * @param canvasWidth 캔버스 너비
     * @param canvasHeight 캔버스 높이
     * @param partitionCount 분단 수
     * @returns 배치 결과
     */
    public static createLayout(
        layoutType: LayoutType,
        totalSeats: number,
        canvasWidth: number = 800,
        canvasHeight: number = 600,
        partitionCount: number = 1
    ): LayoutResult {
        try {
            if (!Number.isFinite(totalSeats) || totalSeats <= 0) {
                return {
                    seats: [],
                    success: false,
                    errorMessage: '좌석 수가 올바르지 않습니다.'
                };
            }

            let seats: Seat[];
            
            switch (layoutType) {
                case LayoutType.SINGLE_UNIFORM:
                    seats = this.createSingleUniformLayout(totalSeats, canvasWidth, canvasHeight, partitionCount);
                    break;
                
                case LayoutType.PAIR_UNIFORM:
                    seats = this.createPairUniformLayout(totalSeats, canvasWidth, canvasHeight, partitionCount);
                    break;
                
                case LayoutType.GROUP:
                    seats = this.createGroupLayout(totalSeats, 4, canvasWidth, canvasHeight);
                    break;

                case LayoutType.GROUP_3:
                    seats = this.createGroupLayout(totalSeats, 3, canvasWidth, canvasHeight);
                    break;

                case LayoutType.GROUP_4:
                    seats = this.createGroupLayout(totalSeats, 4, canvasWidth, canvasHeight);
                    break;

                case LayoutType.GROUP_5:
                    seats = this.createGroupLayout(totalSeats, 5, canvasWidth, canvasHeight);
                    break;

                case LayoutType.GROUP_6:
                    seats = this.createGroupLayout(totalSeats, 6, canvasWidth, canvasHeight);
                    break;
                
                case LayoutType.USHAPE:
                    seats = this.createUShapeLayout(totalSeats, canvasWidth);
                    break;
                
                case LayoutType.CUSTOM:
                    // 사용자 임의 구성은 아직 구현되지 않았다.
                    // 빈 배열을 성공으로 돌려주면 호출부가 정상 배치로 취급한다.
                    return {
                        seats: [],
                        success: false,
                        errorMessage: '사용자 임의 구성은 아직 지원하지 않습니다.'
                    };
                
                default:
                    return {
                        seats: [],
                        success: false,
                        errorMessage: '알 수 없는 배치 유형입니다.'
                    };
            }
            
            if (seats.length === 0) {
                return {
                    seats: [],
                    success: false,
                    errorMessage: '좌석을 생성하지 못했습니다. 설정을 확인해주세요.'
                };
            }

            return {
                seats,
                success: true
            };
        } catch (error) {
            return {
                seats: [],
                success: false,
                errorMessage: error instanceof Error ? error.message : '배치 생성 중 오류가 발생했습니다.'
            };
        }
    }
}

